import { Directory, Filesystem } from '@capacitor/filesystem'
import { RecapImagesRow, RecapImagesRows } from '../types/database'
import { useDatabase } from '../composables/database'
import { useRecapStore } from '../store/useRecapStore'
import { Capacitor } from '@capacitor/core'
import { storeToRefs } from 'pinia'
import { logInfo } from '../utils'
import { Nullish } from '../types/utility'
import { addBreadcrumb } from '@sentry/vue'

export const recapImagesFolder = 'FHS/recap_response_images'

/**
 * Interacts with the recap images table and device recap images folder (mobile only)
 * @returns {RecapImagesModelReturn}
 */
export const RecapImagesModel = (
  setSyncProgress?: (message: string | null, percentage: number) => void
): RecapImagesModelReturn => {
  const { run, query } = useDatabase()
  const recapStore = storeToRefs(useRecapStore())

  async function queryAllRecapImages(): Promise<RecapImagesRows> {
    const sql = `
      SELECT
        *
      FROM
        recap_response_images
    `
    return (await query(sql)) as unknown as RecapImagesRows
  }

  async function getAllRecapImages(): Promise<RecapImageUpload[]> {
    const imageMappings = []
    const sql = `
      SELECT
        *
      FROM
        recap_response_images
    `
    const images = (await query(sql)) as unknown as RecapImagesRows
    for (const image of images) {
      const mappedImageArr = await mapRecapImages(
        [image],
        String(image.recap_id),
        String(image.task_id)
      )
      for (const mapping of mappedImageArr) {
        imageMappings.push(mapping)
      }
    }
    return imageMappings
  }

  async function getRecapImagesForRecap(
    recapId: string,
    taskId: string
  ): Promise<RecapImageUpload[]> {
    let images: RecapImagesRows = []
    try {
      const sql = `
        SELECT
          *
        FROM
          recap_response_images
        WHERE
          recap_id = ${recapId}
          AND task_id = ${taskId}
      `
      images = (await query(sql)) as unknown as RecapImagesRows
      return await mapRecapImages(images, recapId, taskId)
    } catch (err) {
      logInfo('error', 'RecapImagesForRecap', 'Error fetching recap images for recap', {
        err,
        images,
      })
      return []
    }
  }

  async function getRecapImagesForRecapField(
    recapId: string,
    taskId: string,
    fieldInstanceId: string,
    fieldRepeatingIndex: number
  ): Promise<RecapImageUpload[]> {
    let images: RecapImagesRows = []
    try {
      const sql = `
        SELECT
          *
        FROM
          recap_response_images
        WHERE
          recap_id = ${recapId}
          AND task_id = ${taskId}
          AND field_instance_id = ${fieldInstanceId}
          AND field_repeating_index = ${fieldRepeatingIndex}
      `
      images = (await query(sql)) as unknown as RecapImagesRows
      return await mapRecapImages(images, recapId, taskId, fieldInstanceId, fieldRepeatingIndex)
    } catch (err) {
      logInfo('error', 'RecapImagesForField', 'Error fetching recap images for field', {
        err,
        images,
      })
      return []
    }
  }

  async function mapRecapImages(
    images: RecapImagesRows,
    recapId: string,
    taskId: string,
    fieldInstanceId?: string,
    fieldRepeatingIndex?: number
  ): Promise<RecapImageUpload[]> {
    addBreadcrumb({
      level: 'debug',
      category: 'MapRecapImages',
      message: 'mapRecapImages',
      data: {
        images,
        recapId,
        taskId,
        fieldInstanceId,
        fieldRepeatingIndex,
      },
    })
    const imageFiles: RecapImageUpload[] = []
    if (images && images.length) {
      if (Capacitor.isNativePlatform()) {
        try {
          let idx: number = 0
          for (const image of images) {
            if (setSyncProgress) {
              setSyncProgress(
                `Task images (load): ( ${idx + 1}/${images.length} )`,
                22 + (idx / images.length) * 4
              )
            }
            // Yield to UI thread - does NOT skip, just pauses briefly
            // This prevents "Application Not Responding" on Android
            await new Promise((resolve) => setTimeout(resolve, 0))
            const fileData = await fetchFile(image.path)
            if (fileData) {
              imageFiles.push({
                ...image,
                data: fileData,
              })
            } else {
              logInfo('error', 'ImageFileMissing', `Image file not found, skipping`, {
                path: image.path,
                recapId,
                taskId,
                fieldInstanceId,
                fieldRepeatingIndex,
              })
            }
            idx++
          }
        } catch (error) {
          logInfo('error', 'RecapImagesMap', 'Images for task could not be mapped', {
            error,
            recapId,
            taskId,
            images,
            fieldInstanceId,
            fieldRepeatingIndex,
          })
        }
      } else {
        try {
          let idx: number = 0
          for (const img of recapStore.recapImages.value) {
            if (setSyncProgress) {
              setSyncProgress(
                `Task images (load): ( ${idx + 1}/${images.length} )`,
                20 + (idx / images.length) * 2
              )
            }
            if (
              !!img &&
              String(img.recap_id) === String(recapId) &&
              String(img.task_id) === String(taskId) &&
              (fieldInstanceId
                ? String(img.image.field_instance_id) === String(fieldInstanceId)
                : true) &&
              (fieldRepeatingIndex
                ? String(img.image.field_repeating_index) === String(fieldRepeatingIndex)
                : true)
            ) {
              imageFiles.push({
                ...img.image,
                id: Number(img.image.id),
              })
            }
            idx++
          }
        } catch (error) {
          logInfo(
            'error',
            'RecapImages: Error converting images (desktop)',
            'error converting image',
            {
              error,
            }
          )
        }
      }
    }
    return imageFiles
  }

  async function writeRecapImage(
    recapId: string,
    taskId: string,
    fieldInstanceId: string,
    fieldRepeatingIndex: number,
    data: { path: string; metadata: object },
    image: string | Nullish
  ): Promise<void> {
    // Always add a breadcrumb in case something happens
    addBreadcrumb({
      level: 'debug',
      category: 'WriteRecapImage',
      message: 'writeRecapImage',
      data: {
        recapId,
        taskId,
        fieldInstanceId,
        fieldRepeatingIndex,
        data,
        image,
      },
    })

    if (!image) {
      throw new Error('No image provided to write to device')
    }

    // Ensure the directory to store the images is created
    await makeRecapImagesDir()

    const sql = `
      INSERT INTO recap_response_images ('date_uploaded', 'path', 'metadata', 'recap_id', 'task_id', 'field_instance_id', 'field_repeating_index')
      VALUES (date('now', 'localtime'), ?, ?, ?, ?, ?, ?)
    `
    const values: any[] = [
      data.path,
      JSON.stringify(data.metadata),
      recapId,
      taskId,
      fieldInstanceId,
      fieldRepeatingIndex,
    ]

    try {
      await run(sql, values)
      if (Capacitor.isNativePlatform()) {
        // Write the image to the device
        await Filesystem.writeFile({
          data: image,
          path: `${recapImagesFolder}/${data.path}`,
          directory: Directory.Data,
        })
      } else {
        // Add to the recap store
        recapStore.recapImages.value.push({
          task_id: taskId,
          recap_id: recapId,
          image: {
            id: 0, // The ID doesn't matter for Desktop images, so this is strictly to appease TypeScript
            data: image,
            date_uploaded: new Date().toString(),
            metadata: JSON.stringify(data.metadata),
            path: data.path,
            recap_id: Number(recapId),
            task_id: Number(taskId),
            field_instance_id: Number(fieldInstanceId),
            field_repeating_index: Number(fieldRepeatingIndex),
          },
        })
      }
    } catch (error) {
      logInfo('error', 'RecapImagesWrite', 'Error writing recap image', {
        error,
        recapId,
        taskId,
        fieldInstanceId,
        fieldRepeatingIndex,
        data,
      })
      throw new Error(`Error writing recap image: ${error}`)
    }
  }

  // Remove any images over 14 days old
  async function removeStaleRecapImages() {
    try {
      const sql = `
        SELECT
          *
        FROM
          recap_response_images
        WHERE
          date_uploaded <= date('now', '-14 day')
      `
      const staleImages = await query(sql)
      if (staleImages) {
        for (const image of staleImages) {
          try {
            await deleteFile(image.path)
            await run('DELETE FROM recap_response_images WHERE id = ?', [image.id])
          } catch (err) {
            logInfo('error', 'RecapImagesDelete', 'Error deleting stale image', { err })
          }
        }
      }
    } catch (err) {
      logInfo('error', 'RecapImagesStaleDelete', 'Error deleting stale images', { err })
    }
  }

  async function removeRecapImage(path: string): Promise<boolean> {
    // Delete the image from the database
    const deleteSql = 'DELETE FROM recap_response_images WHERE path = ?'
    await run(deleteSql, [path])
    return await deleteFile(path)
  }

  const removeByTaskId = async (taskId: string) => {
    const sql = `SELECT * FROM recap_response_images WHERE task_id = ${taskId}`
    const toDelete: RecapImagesRows | void | null = await query(sql)
    if (toDelete) {
      for (const imgToDelete of toDelete) {
        try {
          await removeRecapImage(imgToDelete.path)
        } catch (err) {
          console.error(`Error deleting recap image: ${err}`)
        }
      }
    }
  }

  // Capacitor only
  async function makeRecapImagesDir(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Filesystem.mkdir({
        path: recapImagesFolder,
        directory: Directory.Data,
        recursive: true,
      }).catch((error) => {
        if (error.message !== 'Directory exists') {
          console.error(`Error creating recap image directory: ${error}`)
        }
      })
    }
  }

  /**
   * Currently this method is only called from the clearQueue, and as such
   * all recap definitions should be deleted by now from the Pinia store.
   * If this ever changes, make sure to remove the images from each definition in the store here.
   */
  async function clearRecapImages() {
    try {
      const sql = 'DELETE FROM recap_response_images'
      await run(sql, [])
      if (Capacitor.isNativePlatform()) {
        try {
          // Try to remove the original images directory
          await Filesystem.rmdir({
            path: recapImagesFolder,
            directory: Directory.Documents,
            recursive: true,
          })
        } catch (err) {
          console.error(`Error removing the original recap images directory: ${err}`)
        }
        try {
          // Try to remove the new images directory
          await Filesystem.rmdir({
            path: recapImagesFolder,
            directory: Directory.Data,
            recursive: true,
          })
        } catch (err) {
          console.error(`Error removing the new recap images directory: ${err}`)
        }
      }
    } catch (err) {
      logInfo('error', 'RecapImagesClear', 'Error clearing recap images', { err })
    }
  }

  async function fetchFile(
    path: string,
    directory: Directory = Directory.Data
  ): Promise<string | null> {
    let fileData: string | null = null
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    try {
      const fileReadTimeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(`File read timed out: ${directory} ${path}`))
        }, 5000)
      })
      const documentFile = await Promise.race([
        Filesystem.readFile({
          path: `${recapImagesFolder}/${path}`,
          directory,
        }),
        fileReadTimeout,
      ])
      clearTimeout(timeoutId)
      if (documentFile && documentFile.data) {
        fileData = documentFile.data
      } else {
        if (timeoutId) clearTimeout(timeoutId)
        addBreadcrumb({
          level: 'debug',
          category: 'FetchImage',
          message: 'File not found or empty',
          data: {
            path,
            directory,
          },
        })
        if (directory === Directory.Documents) {
          // We've now checked both directories and the file is missing or empty
          const err = new Error(
            `Error: File not found or file data empty! Path: ${path}, Directory: ${directory}`
          )
          logInfo('error', 'RecapImagesFetch', 'Error fetching file', { err, path, directory })
          throw err
        }
      }
    } catch (err) {
      clearTimeout(timeoutId)
      const isTimeout = err instanceof Error && err.message.includes('timed out')
      if (isTimeout) {
        logInfo('error', 'RecapImagesDocumentsFetch', 'Timed out fetching file', {
          err,
          path,
          directory,
        })
      } else {
        logInfo('error', 'RecapImagesDocumentsFetch', 'Error fetching file', {
          err,
          path,
          directory,
        })
      }
    }

    // If we don't have the file yet, try using the Documents directory instead (if we haven't already looked)
    if (!fileData && directory !== Directory.Documents) {
      return fetchFile(path, Directory.Documents)
    }

    return fileData
  }

  async function deleteFile(path: string): Promise<boolean> {
    let deleted = false
    if (Capacitor.isNativePlatform()) {
      try {
        // First try deleting the file from the Data directory
        await Filesystem.deleteFile({
          path: `${recapImagesFolder}/${path}`,
          directory: Directory.Data,
        })
        deleted = true
      } catch (err) {
        console.error(`Error deleting file: ${err}`)
      }
      if (!deleted) {
        try {
          // Then try deleting the file from the Documents directory
          await Filesystem.deleteFile({
            path: `${recapImagesFolder}/${path}`,
            directory: Directory.Documents,
          })
          deleted = true
        } catch (err) {
          console.error(`Error deleting file: ${err}`)
        }
      }
      if (!deleted) {
        logInfo('error', 'RecapImageDelete', 'Error deleting image', { path })
      }
    } else {
      const imageLength = recapStore.recapImages.value.length
      if (imageLength) {
        recapStore.recapImages.value = recapStore.recapImages.value.filter(
          (img) => img.image.path !== path
        )
        if (imageLength > recapStore.recapImages.value.length) {
          deleted = true
        }
      }
    }
    return deleted
  }

  return {
    getAllRecapImages,
    getRecapImagesForRecap,
    getRecapImagesForRecapField,
    writeRecapImage,
    removeRecapImage,
    removeStaleRecapImages,
    removeByTaskId,
    clearRecapImages,
    queryAllRecapImages,
  }
}

type RecapImagesModelReturn = {
  getAllRecapImages: () => Promise<RecapImageUpload[]>
  getRecapImagesForRecap: (recapId: string, taskId: string) => Promise<RecapImageUpload[]>
  getRecapImagesForRecapField: (
    recapId: string,
    taskId: string,
    fieldInstanceId: string,
    fieldRepeatingIndex: number
  ) => Promise<RecapImageUpload[]>
  writeRecapImage: (
    recapId: string,
    taskId: string,
    fieldInstanceId: string,
    fieldRepeatingIndex: number,
    data: { path: string; metadata: object },
    image: string | Nullish
  ) => Promise<void>
  removeRecapImage: (path: string) => Promise<boolean>
  removeStaleRecapImages: () => Promise<void>
  removeByTaskId: (taskId: string) => Promise<void>
  clearRecapImages: () => Promise<void>
  queryAllRecapImages: () => Promise<RecapImagesRows>
}

export type RecapImageUpload = RecapImagesRow & {
  data: string
}

export type FileReference = {
  id: string
  data: string
}
